import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardActionArea,
  Paper,
  Typography,
  Button,
  IconButton,
  Tabs,
  Tab,
  Chip,
  Menu,
  MenuItem,
  ListItemIcon,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  TextField,
  Autocomplete,
  InputAdornment,
  CircularProgress,
  Snackbar,
  Alert,
  alpha,
} from '@mui/material';
import {
  ArrowBack,
  Add,
  MoreVert,
  EditOutlined,
  ArchiveOutlined,
  UnarchiveOutlined,
  ChevronRight,
  TollOutlined,
  AccountBalanceWalletOutlined,
  AddCardOutlined,
  WarningAmberOutlined,
} from '@mui/icons-material';
import { useSupabase } from '../../contexts/SupabaseContext';
import { FASTAG_ISSUERS, summariseTag } from '../../utils/fastag';
import { formatRupees } from '../../utils/fuelEfficiency';
import GuidedTour from '../../components/tour/GuidedTour';
import TourButton from '../../components/tour/TourButton';
import useTour from '../../components/tour/useTour';
import { TOURS } from '../../components/tour/tours';
import AmountField from '../../components/AmountField';

const emptyForm = { vehicleNumber: '', vehicleName: '', issuer: '', tagId: '', openingBalance: '', lowBalanceAlert: '200' };

export const SummaryTile = ({ icon, color, value, label }) => (
  <Box sx={{ p: { xs: 1.5, sm: 2 }, borderRadius: 2, bgcolor: (t) => alpha(t.palette[color].main, 0.06), minWidth: 0 }}>
    <Box sx={{ color: `${color}.main`, display: 'flex', mb: 0.75, '& svg': { fontSize: 22 } }}>{icon}</Box>
    <Typography sx={{ fontWeight: 700, fontSize: { xs: '1.125rem', sm: '1.375rem' }, fontVariantNumeric: 'tabular-nums' }} noWrap>
      {value}
    </Typography>
    <Typography variant="caption" color="text.secondary">{label}</Typography>
  </Box>
);

const FastagManager = () => {
  const navigate = useNavigate();
  const { supabase, user } = useSupabase();

  const [tab, setTab] = useState('active');
  const [tags, setTags] = useState([]);
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const fastagTour = useTour('fastag', { ready: !loading });

  const [vehicles, setVehicles] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingTag, setEditingTag] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [formError, setFormError] = useState('');
  const [saving, setSaving] = useState(false);

  const [menuAnchor, setMenuAnchor] = useState(null);
  const [menuTag, setMenuTag] = useState(null);
  const [confirmTag, setConfirmTag] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const notify = (message, severity = 'success') => setSnackbar({ open: true, message, severity });

  const fetchTags = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    const [tagsResult, entriesResult] = await Promise.all([
      supabase
        .from('fastags')
        .select('*')
        .eq('user_id', user.id)
        .eq('is_archived', tab === 'archived')
        .order('created_at', { ascending: false }),
      supabase
        .from('fastag_entries')
        .select('fastag_id, entry_type, amount, date')
        .eq('user_id', user.id),
    ]);

    if (tagsResult.error) {
      console.error('Error fetching FASTags:', tagsResult.error);
      notify('Failed to fetch FASTags', 'error');
    } else {
      setTags(tagsResult.data || []);
    }
    if (entriesResult.error) console.error('Error fetching FASTag entries:', entriesResult.error);
    else setEntries(entriesResult.data || []);
    setLoading(false);
  }, [supabase, user, tab]);

  useEffect(() => {
    fetchTags();
  }, [fetchTags]);

  // Vehicles from Fuel Efficiency, offered as quick picks in the add dialog
  useEffect(() => {
    if (!user) return;
    supabase
      .from('vehicles')
      .select('name, vehicle_number')
      .eq('user_id', user.id)
      .eq('is_archived', false)
      .then(({ data }) => setVehicles((data || []).filter(v => v.vehicle_number)));
  }, [supabase, user]);

  const summaries = useMemo(() => {
    const byTag = {};
    entries.forEach((entry) => {
      (byTag[entry.fastag_id] = byTag[entry.fastag_id] || []).push(entry);
    });
    return Object.fromEntries(tags.map(tag => [tag.id, summariseTag(tag, byTag[tag.id])]));
  }, [tags, entries]);

  const totals = useMemo(() => Object.values(summaries).reduce((acc, s) => ({
    balance: acc.balance + s.balance,
    monthTolls: acc.monthTolls + s.monthTolls,
    monthRecharged: acc.monthRecharged + s.monthRecharged,
    low: acc.low + (s.isLow ? 1 : 0),
  }), { balance: 0, monthTolls: 0, monthRecharged: 0, low: 0 }), [summaries]);

  // ---- Add / edit ----
  const openAddDialog = () => {
    setEditingTag(null);
    setForm(emptyForm);
    setFormError('');
    setDialogOpen(true);
  };

  const openEditDialog = (tag) => {
    setEditingTag(tag);
    setForm({
      vehicleNumber: tag.vehicle_number || '',
      vehicleName: tag.vehicle_name || '',
      issuer: tag.issuer || '',
      tagId: tag.tag_id || '',
      openingBalance: String(tag.opening_balance ?? ''),
      lowBalanceAlert: String(tag.low_balance_alert ?? ''),
    });
    setFormError('');
    setDialogOpen(true);
  };

  const closeDialog = () => {
    if (saving) return;
    setDialogOpen(false);
    setEditingTag(null);
    setForm(emptyForm);
  };

  const handleSave = async () => {
    if (!form.vehicleNumber.trim()) {
      setFormError('Please enter the vehicle number');
      return;
    }
    const openingBalance = form.openingBalance === '' ? 0 : parseFloat(form.openingBalance);
    const lowBalanceAlert = form.lowBalanceAlert === '' ? 0 : parseFloat(form.lowBalanceAlert);
    if (!isFinite(openingBalance) || !isFinite(lowBalanceAlert) || lowBalanceAlert < 0) {
      setFormError('Please enter valid amounts');
      return;
    }
    setSaving(true);
    const payload = {
      vehicle_number: form.vehicleNumber.trim().toUpperCase(),
      vehicle_name: form.vehicleName.trim(),
      issuer: form.issuer.trim(),
      tag_id: form.tagId.trim(),
      opening_balance: openingBalance,
      low_balance_alert: lowBalanceAlert,
    };
    const { error } = editingTag
      ? await supabase.from('fastags').update(payload).eq('id', editingTag.id).eq('user_id', user.id)
      : await supabase.from('fastags').insert({ ...payload, user_id: user.id });
    setSaving(false);

    if (error) {
      console.error('Error saving FASTag:', error);
      setFormError(error.code === '23505'
        ? 'A FASTag for this vehicle number already exists'
        : `Failed to ${editingTag ? 'update' : 'save'} FASTag`);
      return;
    }

    setDialogOpen(false);
    setEditingTag(null);
    setForm(emptyForm);
    notify(editingTag ? 'FASTag updated successfully' : 'FASTag added successfully');
    if (tab !== 'active' && !editingTag) setTab('active');
    else fetchTags();
  };

  // ---- Archive / restore ----
  const handleArchiveToggle = async (tag) => {
    const archive = !tag.is_archived;
    const { error } = await supabase.from('fastags').update({ is_archived: archive }).eq('id', tag.id).eq('user_id', user.id);
    setConfirmTag(null);
    if (error) {
      console.error('Error updating FASTag archive status:', error);
      notify(archive ? 'Failed to archive FASTag' : 'Failed to restore FASTag', 'error');
      return;
    }
    notify(archive ? 'FASTag archived successfully' : 'FASTag restored successfully');
    fetchTags();
  };

  const openMenu = (event, tag) => {
    event.stopPropagation();
    setMenuAnchor(event.currentTarget);
    setMenuTag(tag);
  };
  const closeMenu = () => setMenuAnchor(null);

  const setField = (field) => (e) => { setForm({ ...form, [field]: e.target.value }); setFormError(''); };

  // ---- Render ----
  const emptyState = tab === 'active' ? (
    <Card sx={{ p: { xs: 3, sm: 5 }, textAlign: 'center' }}>
      <Box sx={{ width: 88, height: 88, borderRadius: '50%', mx: 'auto', mb: 2, display: 'grid', placeItems: 'center', bgcolor: (t) => alpha(t.palette.primary.main, 0.08), color: 'primary.main' }}>
        <TollOutlined sx={{ fontSize: 44 }} />
      </Box>
      <Typography variant="h6" sx={{ fontWeight: 700 }}>No FASTags Added</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mt: 1, mb: 3, maxWidth: 420, mx: 'auto' }}>
        Add your vehicle's FASTag to keep track of its balance, recharges and toll charges.
      </Typography>
      <Button variant="contained" startIcon={<Add />} onClick={openAddDialog}>Add Your First FASTag</Button>
    </Card>
  ) : (
    <Card sx={{ p: 5, textAlign: 'center' }}>
      <ArchiveOutlined sx={{ fontSize: 48, color: 'text.disabled' }} />
      <Typography variant="h6" sx={{ fontWeight: 700, mt: 1 }}>No Archived FASTags</Typography>
      <Typography variant="body2" color="text.secondary">Archived FASTags will appear here.</Typography>
    </Card>
  );

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', alignItems: { xs: 'flex-start', sm: 'center' }, justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <IconButton onClick={() => navigate('/utilities')} aria-label="Back to Utilities">
            <ArrowBack />
          </IconButton>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 700 }}>FASTag</Typography>
            <Typography variant="body2" color="text.secondary">Balances, recharges and toll charges</Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <TourButton onClick={fastagTour.start} />
          <Button variant="contained" startIcon={<Add />} onClick={openAddDialog} data-tour="fastag-add">Add FASTag</Button>
        </Box>
      </Box>

      {tab === 'active' && tags.length > 0 && (
        <Paper data-tour="fastag-summary" sx={{ p: { xs: 1.5, sm: 2 }, mb: 2.5, display: 'grid', gridTemplateColumns: { xs: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' }, gap: { xs: 1, sm: 1.5 } }}>
          <SummaryTile icon={<AccountBalanceWalletOutlined />} color="primary" value={formatRupees(totals.balance, 0)} label="Total balance" />
          <SummaryTile icon={<TollOutlined />} color="error" value={formatRupees(totals.monthTolls, 0)} label="Tolls this month" />
          <SummaryTile icon={<AddCardOutlined />} color="success" value={formatRupees(totals.monthRecharged, 0)} label="Recharged this month" />
          <SummaryTile icon={<WarningAmberOutlined />} color={totals.low ? 'warning' : 'info'} value={totals.low} label={totals.low === 1 ? 'Tag low on balance' : 'Tags low on balance'} />
        </Paper>
      )}

      <Tabs value={tab} onChange={(e, value) => setTab(value)} data-tour="fastag-tabs" sx={{ mb: 2.5, borderBottom: 1, borderColor: 'divider' }}>
        <Tab value="active" label="Active" />
        <Tab value="archived" label="Archived" icon={<ArchiveOutlined fontSize="small" />} iconPosition="start" />
      </Tabs>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
      ) : tags.length === 0 ? (
        emptyState
      ) : (
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' }, gap: 2 }}>
          {tags.map((tag) => {
            const archived = tag.is_archived;
            const summary = summaries[tag.id] || summariseTag(tag);
            const content = (
              <Box sx={{ p: 2.25, display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                <Box sx={{
                  width: 52, height: 52, borderRadius: '50%', flexShrink: 0, display: 'grid', placeItems: 'center',
                  bgcolor: (t) => alpha(archived ? t.palette.text.secondary : t.palette.primary.main, 0.08),
                  color: archived ? 'text.secondary' : 'primary.main',
                }}>
                  <TollOutlined sx={{ fontSize: 28 }} />
                </Box>
                <Box sx={{ minWidth: 0, flexGrow: 1 }}>
                  <Typography sx={{ fontWeight: 700, fontFamily: 'monospace', letterSpacing: 0.5 }} noWrap>{tag.vehicle_number}</Typography>
                  <Typography variant="body2" color="text.secondary" noWrap>
                    {[tag.vehicle_name, tag.issuer].filter(Boolean).join(' · ') || 'FASTag'}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.75, flexWrap: 'wrap' }}>
                    <Typography sx={{ fontWeight: 700, color: summary.balance < 0 ? 'error.main' : 'text.primary', fontVariantNumeric: 'tabular-nums' }}>
                      {formatRupees(summary.balance)}
                    </Typography>
                    {!archived && summary.isLow && (
                      <Chip label="Low balance" size="small" color="warning" icon={<WarningAmberOutlined />} />
                    )}
                  </Box>
                </Box>
                {!archived && <ChevronRight sx={{ color: 'text.secondary' }} />}
              </Box>
            );

            return (
              <Card key={tag.id} data-tour="fastag-card" sx={{ position: 'relative', borderRadius: 3, '&:hover': { boxShadow: archived ? undefined : 'var(--fx-card-shadow-hover)' } }}>
                {archived ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', pr: 1.5 }}>
                    {content}
                    <Button size="small" variant="outlined" startIcon={<UnarchiveOutlined />} onClick={() => handleArchiveToggle(tag)} sx={{ flexShrink: 0 }}>
                      Restore
                    </Button>
                  </Box>
                ) : (
                  <>
                    <CardActionArea onClick={() => navigate(`/utilities/fastag/${tag.id}`)} sx={{ pr: 5 }}>
                      {content}
                    </CardActionArea>
                    <IconButton
                      size="small"
                      onClick={(e) => openMenu(e, tag)}
                      aria-label={`Options for ${tag.vehicle_number}`}
                      sx={{ position: 'absolute', top: 8, right: 8 }}
                    >
                      <MoreVert fontSize="small" />
                    </IconButton>
                  </>
                )}
              </Card>
            );
          })}
        </Box>
      )}

      {/* Tag options */}
      <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={closeMenu}>
        <MenuItem onClick={() => { closeMenu(); openEditDialog(menuTag); }}>
          <ListItemIcon><EditOutlined fontSize="small" /></ListItemIcon>
          Edit FASTag
        </MenuItem>
        <MenuItem onClick={() => { closeMenu(); setConfirmTag(menuTag); }} sx={{ color: 'error.main' }}>
          <ListItemIcon><ArchiveOutlined fontSize="small" color="error" /></ListItemIcon>
          Archive FASTag
        </MenuItem>
      </Menu>

      {/* Add / edit tag */}
      <Dialog open={dialogOpen} onClose={closeDialog} maxWidth="xs" fullWidth>
        <DialogTitle>{editingTag ? 'Edit FASTag' : 'Add FASTag'}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.25, pt: 1 }}>
            <Autocomplete
              freeSolo
              options={vehicles}
              getOptionLabel={(option) => (typeof option === 'string' ? option : option.vehicle_number)}
              renderOption={(props, option) => (
                <li {...props} key={option.vehicle_number}>
                  <Box>
                    <Typography sx={{ fontFamily: 'monospace' }}>{option.vehicle_number}</Typography>
                    <Typography variant="caption" color="text.secondary">{option.name}</Typography>
                  </Box>
                </li>
              )}
              inputValue={form.vehicleNumber}
              onInputChange={(e, value) => { setForm(prev => ({ ...prev, vehicleNumber: value.toUpperCase() })); setFormError(''); }}
              onChange={(e, value) => {
                if (value && typeof value === 'object') {
                  setForm(prev => ({ ...prev, vehicleNumber: value.vehicle_number, vehicleName: prev.vehicleName || value.name }));
                }
              }}
              renderInput={(params) => (
                <TextField {...params} label="Vehicle Number" required autoFocus placeholder="e.g., MH 01 AB 1234" />
              )}
            />
            <TextField
              label="Vehicle Name (Optional)"
              placeholder="e.g., Honda City"
              value={form.vehicleName}
              onChange={setField('vehicleName')}
            />
            <Autocomplete
              freeSolo
              options={FASTAG_ISSUERS}
              inputValue={form.issuer}
              onInputChange={(e, value) => setForm(prev => ({ ...prev, issuer: value }))}
              renderInput={(params) => <TextField {...params} label="Issuer Bank (Optional)" placeholder="e.g., ICICI Bank" />}
            />
            <TextField
              label="Tag ID (Optional)"
              placeholder="Printed on the tag or in the bank app"
              value={form.tagId}
              onChange={setField('tagId')}
            />
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
              <AmountField
                label={editingTag ? 'Opening Balance' : 'Current Balance'}
                type="number"
                inputProps={{ step: 'any', inputMode: 'decimal' }}
                placeholder="0"
                InputProps={{ startAdornment: <InputAdornment position="start">₹</InputAdornment> }}
                value={form.openingBalance}
                onChange={setField('openingBalance')}
              />
              <AmountField
                label="Low Balance Alert"
                type="number"
                inputProps={{ min: 0, step: 'any', inputMode: 'decimal' }}
                InputProps={{ startAdornment: <InputAdornment position="start">₹</InputAdornment> }}
                value={form.lowBalanceAlert}
                onChange={setField('lowBalanceAlert')}
              />
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ mt: -1 }}>
              {editingTag
                ? 'The balance before your first recorded recharge or toll.'
                : "Enter the tag's balance today. Recharges and tolls you add later change it."}
            </Typography>
            {formError && <Alert severity="error">{formError}</Alert>}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog} disabled={saving}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving…' : editingTag ? 'Update' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Archive confirmation */}
      <Dialog open={Boolean(confirmTag)} onClose={() => setConfirmTag(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Archive FASTag</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to archive the FASTag for "{confirmTag?.vehicle_number}"? Its history is kept and you can restore it from the Archived tab.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmTag(null)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={() => handleArchiveToggle(confirmTag)}>Archive</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={3000} onClose={() => setSnackbar({ ...snackbar, open: false })} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity={snackbar.severity} variant="filled" onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>

      <GuidedTour steps={TOURS.fastag} open={fastagTour.open} onClose={fastagTour.close} />
    </Box>
  );
};

export default FastagManager;
